import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { RmButton, RmEditorHeader, RmEditorPage, RmFixList, RmFooter } from './EditorShell';
import { POST_OP_PATIENTS, REMINDERS } from '../../settings-reminders/mockData';
import { RM_CHANNEL_ICON, RM_CHANNEL_LABEL } from '../../settings-reminders/types';
import type { RmPreviewAppointment, RmSection, RmStep } from '../../settings-reminders/types';
import {
  VERSION_LABEL,
  bodyFor,
  callFallsBackToText,
  previewLanguage,
  resolveForAppointment,
  sectionIssues,
  showsConfirmAffordance,
  stepReaches,
  subjectFor,
  versionForAppointment,
  whenExplanation,
  whenLabel,
} from '../../settings-reminders/derive';
import { activeLanguages, isRtl, languageName, reviewedLine } from '../../platform-languages';
import type { LanguageCode } from '../../platform-languages';

export interface RmTestViewProps {
  section: RmSection;
  onBack: () => void;
  onSave: () => void;
  /** Opens a named step back on the Messages page (spec 23 §4.2). */
  onEditStep: (stepId: string) => void;
}

type Version = 'not_confirmed' | 'confirmed';

interface SentRow {
  id: string;
  stepIndex: number;
  channel: string;
  version: Version;
  language: LanguageCode;
  status: 'delivered' | 'failed';
  reason?: string;
  at: string;
}

/** Seeded, so the shape of the list — including a failure and its reason — shows. */
const SEEDED: SentRow[] = [
  { id: 't1', stepIndex: 0, channel: 'email', version: 'not_confirmed', language: 'en', status: 'delivered', at: '11:54' },
  { id: 't2', stepIndex: 3, channel: 'text', version: 'confirmed', language: 'fa', status: 'delivered', at: '11:56' },
];

/**
 * Step 2 — Test and save (amendment 25 §4, board `RM2Test`).
 *
 * Built from the recall builder's test page, with the two things a reminder has
 * that a recall does not: **a version** (confirmed or not) and **a language**.
 * Both appear three times over — on the preview-as chips, in the right-hand
 * column, and on the send-to-yourself form — because all three are answering
 * *which of the several messages this step can send am I looking at*.
 */
export function RmTestView({ section, onBack, onSave, onEditStep }: RmTestViewProps) {
  const postOp = section.kind === 'post_op';
  const appointments = postOp ? POST_OP_PATIENTS : REMINDERS.previewAppointments;
  const [apptId, setApptId] = useState(appointments[0]?.id);
  const [stepId, setStepId] = useState(section.steps[0]?.id);
  const [phone, setPhone] = useState('(210) 555-0100');
  const [email, setEmail] = useState('amir@elva.ai');
  const [sendVersion, setSendVersion] = useState<Version>('not_confirmed');
  const [sendLanguage, setSendLanguage] = useState<LanguageCode>('en');
  const [sent, setSent] = useState<SentRow[]>(SEEDED);

  const issues = sectionIssues(section);
  const appt = appointments.find((a) => a.id === apptId) ?? appointments[0];
  const stepIndex = section.steps.findIndex((s) => s.id === stepId);
  const step = section.steps[stepIndex] ?? section.steps[0];

  const send = (i: number, s: RmStep) => {
    const now = new Date();
    setSent((prev: SentRow[]) => [
      {
        id: `t${String(Date.now())}${String(i)}`,
        stepIndex: i,
        channel: RM_CHANNEL_LABEL[s.channel].toLowerCase(),
        version: sendVersion,
        language: sendLanguage,
        status: 'delivered',
        at: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
      },
      ...prev,
    ]);
  };

  return (
    <RmEditorPage>
      <RmEditorHeader
        active="test"
        crumb={`Settings › Appointment Reminders › ${section.title}`}
        titleLead="Check it, then"
        titleAccent="save"
        sub="See each step the way a patient will — in each version and language — and send it to yourself."
        onGoTo={(page) => {
          if (page === 'messages') onBack();
        }}
      />

      <div className="mt-5 flex items-start gap-5 max-[1100px]:flex-col">
        {/* the preview */}
        <div className="min-w-0 flex-1 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)]">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border-default)] px-5 py-3.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11.5px] font-semibold text-[var(--text-muted)]">Preview as</span>
              {appointments.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    setApptId(a.id);
                  }}
                  className={cn(
                    'rounded-[10px] px-2.5 py-1 text-left',
                    a.id === appt?.id
                      ? 'bg-[var(--brand-indigo)] text-white'
                      : 'bg-[var(--surface-sunken)] text-[var(--text-secondary)]',
                  )}
                >
                  <span className="block text-[12px] font-semibold">{a.name}</span>
                  {/* The second line is the whole point of the chip: which version
                      and which language this one is going to get. */}
                  <span className={cn('block text-[10px]', a.id === appt?.id ? 'text-white/80' : 'text-[var(--text-muted)]')}>
                    {a.confirmed ? 'confirmed' : 'not confirmed'} · {languageName(a.language)}
                  </span>
                </button>
              ))}
            </div>
            <span className="text-[11px] text-[var(--text-muted)]">
              Real appointments this week — shown here only, never sent.
            </span>
          </header>

          {step === undefined || appt === undefined ? (
            <p className="m-0 px-5 py-10 text-center text-[13px] text-[var(--text-muted)]">
              Add a step on the previous page to preview it here.
            </p>
          ) : (
            <StepPreview
              section={section}
              step={step}
              index={stepIndex < 0 ? 0 : stepIndex}
              appt={appt}
              onEdit={() => {
                onEditStep(step.id);
              }}
            />
          )}
        </div>

        {/* the steps, and sending one to yourself */}
        <div className="w-[392px] shrink-0 max-[1100px]:w-full">
          <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">
              The {postOp ? 'checks' : 'steps'}
            </div>
            <div className="mt-2 flex flex-col">
              {section.steps.map((s) => {
                const mine = issues.filter((x) => x.stepId === s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setStepId(s.id);
                    }}
                    className={cn(
                      'flex items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left',
                      s.id === step?.id ? 'bg-[var(--brand-indigo-tint)]' : 'hover:bg-[var(--surface-sunken)]',
                    )}
                  >
                    <Icon
                      kind={RM_CHANNEL_ICON[s.channel] as IconKind}
                      size={13}
                      stroke={2}
                      className="shrink-0 text-[var(--text-muted)]"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">
                        {RM_CHANNEL_LABEL[s.channel]}
                      </span>
                      <span className="ml-1.5 font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">
                        {whenLabel(s, section.kind)}
                      </span>
                    </span>
                    {!s.on ? (
                      <span className="shrink-0 text-[11px] text-[var(--text-muted)]">Off</span>
                    ) : mine.length === 0 ? (
                      <span className="shrink-0 text-[11.5px] font-semibold text-[var(--money-green)]">
                        ✓ Ready
                        {s.twoVersions === true ? ' · 2 versions' : ''}
                        {s.audience === 'not_confirmed' ? ' · not confirmed only' : ''}
                      </span>
                    ) : (
                      <span className="shrink-0 text-[11px] font-semibold text-[var(--alert-red)]">{mine[0]?.short}</span>
                    )}
                    <span
                      role="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditStep(s.id);
                      }}
                      className="shrink-0 text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
                    >
                      Edit
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
            <div className="text-[12.5px] font-bold text-[var(--text-primary)]">Send it to yourself</div>
            <p className="m-0 mt-1 text-[11px] leading-[1.55] text-[var(--text-muted)]">
              A made-up appointment tomorrow at 10:00, marked [Test].{' '}
              {section.kind === 'before' ? 'The Confirm button opens a test page and confirms nothing.' : null}
            </p>
            <div className="mt-2.5 flex flex-col gap-2">
              <Field label="Phone">
                <input
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                  }}
                  className="mt-1 w-full rounded-[9px] border border-[var(--border-default)] px-2.5 py-1.5 text-[12.5px] font-normal text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
                />
              </Field>
              <Field label="Email">
                <input
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  className="mt-1 w-full rounded-[9px] border border-[var(--border-default)] px-2.5 py-1.5 text-[12.5px] font-normal text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
                />
              </Field>
              <div className="flex gap-2">
                {/* A step with two versions can send either. Without this the test
                    could only ever exercise one of the two messages. */}
                {section.kind === 'before' ? (
                  <Field label="Version">
                    <select
                      value={sendVersion}
                      aria-label="Version to send"
                      onChange={(e) => {
                        setSendVersion(e.target.value as Version);
                      }}
                      className="mt-1 w-full rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-2 py-1.5 text-[12.5px] font-normal text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
                    >
                      <option value="not_confirmed">{VERSION_LABEL.not_confirmed}</option>
                      <option value="confirmed">{VERSION_LABEL.confirmed}</option>
                    </select>
                  </Field>
                ) : null}
                <Field label="Language">
                  <select
                    value={sendLanguage}
                    aria-label="Language to send in"
                    onChange={(e) => {
                      setSendLanguage(e.target.value as LanguageCode);
                    }}
                    className="mt-1 w-full rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-2 py-1.5 text-[12.5px] font-normal text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
                  >
                    {activeLanguages().map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {step === undefined ? null : (
                <RmButton
                  onClick={() => {
                    send(stepIndex < 0 ? 0 : stepIndex, step);
                  }}
                >
                  Send {postOp ? 'check' : 'step'} {stepIndex + 1}
                </RmButton>
              )}
              <RmButton
                kind="soft"
                onClick={() => {
                  section.steps.forEach((s, i) => {
                    send(i, s);
                  });
                }}
              >
                Send every {postOp ? 'check' : 'step'}
              </RmButton>
            </div>
            <p className="m-0 mt-2 text-[11px] leading-[1.55] text-[var(--text-muted)]">
              Every step goes now, one minute apart. The AI call rings your phone.
            </p>

            {sent.length > 0 ? (
              <div className="mt-3 border-t border-[var(--border-default)] pt-2.5">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">
                  Sent in the last hour
                </div>
                <div className="mt-1.5 flex flex-col gap-1">
                  {sent.slice(0, 6).map((r) => (
                    <div key={r.id} className="flex items-baseline justify-between gap-2 text-[11.5px]">
                      {/* Step · channel · version · language · status — every axis
                          the send could vary on, so two rows are never ambiguous. */}
                      <span className="min-w-0 truncate text-[var(--text-secondary)]">
                        Step {r.stepIndex + 1} · {r.channel} ·{' '}
                        {r.version === 'confirmed' ? 'confirmed' : 'not confirmed'} · {languageName(r.language)} ·{' '}
                        <b
                          className="font-semibold"
                          style={{ color: r.status === 'delivered' ? 'var(--money-green)' : 'var(--alert-red)' }}
                        >
                          {r.status}
                        </b>
                        {r.reason === undefined ? '' : ` — ${r.reason}`}
                      </span>
                      <span className="shrink-0 font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">
                        {r.at}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* the save card */}
      <div
        className="mt-5 overflow-hidden rounded-[16px] border bg-[var(--surface-card)] px-5 py-4"
        style={{
          borderColor: issues.length === 0 ? 'var(--money-green)' : 'var(--alert-red)',
          borderTopWidth: 3,
        }}
      >
        {issues.length === 0 ? (
          <div className="flex items-center gap-1.5 text-[13px] font-bold text-[var(--money-green)]">
            <Icon kind="check" size={14} stroke={3} />
            Every {postOp ? 'check' : 'step'} is ready
          </div>
        ) : (
          <RmFixList issues={issues} onGoToStep={onEditStep} />
        )}
        <div className="mt-2 text-[12.5px] text-[var(--text-secondary)]">
          Changes apply to messages not sent yet —{' '}
          <b className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">
            {REMINDERS.appointmentsNext14Days}
          </b>{' '}
          appointments in the next 14 days.
        </div>
      </div>

      <RmFooter>
        <RmButton onClick={onBack}>Back</RmButton>
        <RmButton
          kind="primary"
          disabled={issues.length > 0}
          title={issues.length > 0 ? 'Fix everything listed above first' : undefined}
          onClick={onSave}
        >
          Save changes
        </RmButton>
      </RmFooter>
    </RmEditorPage>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex-1 text-[11px] font-semibold text-[var(--text-muted)]">
      {label}
      {children}
    </label>
  );
}

/** One real preview per channel, in the version and language this one will get. */
function StepPreview({
  section,
  step,
  index,
  appt,
  onEdit,
}: {
  section: RmSection;
  step: RmStep;
  index: number;
  appt: RmPreviewAppointment;
  onEdit: () => void;
}) {
  const language = previewLanguage(appt, false);
  const rtl = isRtl(language);
  const version = versionForAppointment(step, appt);
  const reaches = stepReaches(step, appt);
  const callToText = callFallsBackToText(step, language);
  const resolved = resolveForAppointment(bodyFor(step, version), appt, step.fallbacks);
  const subject = resolveForAppointment(subjectFor(step, version), appt, step.fallbacks);
  const cta = showsConfirmAffordance(step, version) && section.kind === 'before';

  const words =
    step.channel === 'ai_call' && !callToText
      ? (appt.rendered?.spoken ?? 'Add a brief to see how it reads')
      : language !== 'en' && appt.rendered !== undefined
        ? ((version === 'confirmed' ? appt.rendered.confirmed : appt.rendered.notConfirmed) ?? resolved.text)
        : resolved.text;

  return (
    <div className="px-5 py-4">
      <div className="text-[13px] font-bold text-[var(--text-primary)]">
        {section.kind === 'post_op' ? 'Check' : 'Step'} {index + 1} · {RM_CHANNEL_LABEL[step.channel]} ·{' '}
        {whenLabel(step, section.kind)}
      </div>
      <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">
        {step.mode === 'smart'
          ? 'Smart — ELVA writes this for each patient'
          : 'Template — every patient gets these words, with their own details'}
      </div>

      {!reaches ? (
        <p className="m-0 mt-4 text-[12.5px] italic text-[var(--text-muted)]">
          {appt.name} doesn’t get this step.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap items-start gap-5">
          <div className="w-[320px] shrink-0">
            {step.channel === 'email' ? (
              <InboxFrame
                from={appt.values.practice_name ?? 'The practice'}
                subject={subject.text}
                rtl={rtl}
                cta={cta}
              >
                {words}
              </InboxFrame>
            ) : step.channel === 'ai_call' && !callToText ? (
              <CallFrame>{words}</CallFrame>
            ) : (
              <PhoneFrame practice={appt.values.practice_name ?? 'The practice'} rtl={rtl}>
                {words}
                <div className="mt-1.5 text-[10.5px] opacity-80">
                  {cta ? <div>{reviewedLine('confirm_text', language)}</div> : null}
                  <div>{reviewedLine('opt_out', language)}</div>
                </div>
              </PhoneFrame>
            )}
            {callToText ? (
              <div className="mt-2 text-[11px] text-[var(--warn-amber)]">
                ELVA can’t call in {languageName(language)} yet — the text version above is what goes.
              </div>
            ) : null}
          </div>

          <div className="min-w-[240px] flex-1">
            <Detail label="Version">
              {VERSION_LABEL[version]} — {appt.name.split(' ')[0]}{' '}
              {appt.confirmed ? 'has confirmed' : 'hasn’t confirmed'}.
              {cta && step.channel === 'email' ? ' The two buttons are added to this version automatically.' : null}
            </Detail>
            <Detail label="When">{whenExplanation(step, section.kind)}</Detail>
            {/* The language, named — a preview that silently rendered English for a
                Farsi patient would look right and be wrong. */}
            <Detail label="Language">{languageName(language)} — {appt.name.split(' ')[0]}’s language on file</Detail>
            {resolved.used.length > 0 ? (
              <Detail label="What each field became">
                <span className="mt-1 flex flex-col gap-1">
                  {resolved.used.map((u, i) => (
                    <span key={`${u.field}-${String(i)}`} className="flex items-baseline gap-2 text-[11.5px]">
                      <span className="rounded-[5px] bg-[var(--brand-indigo-tint)] px-1.5 py-0.5 font-semibold text-[var(--brand-indigo)]">
                        {u.label}
                      </span>
                      <span className="text-[var(--text-secondary)]">
                        {u.value === '' ? '—' : u.value}
                        {u.wasFallback ? (
                          <span className="ml-1 text-[var(--warn-amber)]">
                            — none on file, so this is what we say
                          </span>
                        ) : null}
                      </span>
                    </span>
                  ))}
                </span>
              </Detail>
            ) : null}
            <button
              type="button"
              onClick={onEdit}
              className="mt-2 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
            >
              Edit this step →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2.5">
      <div className="text-[10px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{label}</div>
      <div className="mt-0.5 text-[12px] leading-[1.6] text-[var(--text-secondary)]">{children}</div>
    </div>
  );
}

function PhoneFrame({
  practice,
  rtl,
  children,
}: {
  practice: string;
  rtl: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[18px] border border-[var(--border-default)] bg-[#FAFBFE] p-3">
      <div className="text-center text-[11px] font-semibold text-[var(--text-muted)]">{practice}</div>
      <div
        dir={rtl ? 'rtl' : undefined}
        className="mt-2 rounded-[14px] rounded-bl-[4px] bg-[var(--brand-indigo)] px-3 py-2 text-[12px] leading-[1.6] text-white"
      >
        {children}
      </div>
    </div>
  );
}

function InboxFrame({
  from,
  subject,
  rtl,
  cta,
  children,
}: {
  from: string;
  subject: string;
  rtl: boolean;
  cta: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[12px] border border-[var(--border-default)]">
      <div className="border-b border-[var(--border-default)] bg-[#FAFBFE] px-3 py-2" dir={rtl ? 'rtl' : undefined}>
        <div className="text-[11px] text-[var(--text-muted)]">{from}</div>
        <div className="text-[12.5px] font-bold text-[var(--text-primary)]">{subject}</div>
      </div>
      <div dir={rtl ? 'rtl' : undefined} className="px-3 py-2.5 text-[12px] leading-[1.7] text-[var(--text-secondary)]">
        {children}
        {/* A real button and a real link, not the words for them. ELVA adds both
            to every not-confirmed email; nobody writes them. */}
        {cta ? (
          <span className="mt-2.5 flex items-center gap-2.5">
            <span className="inline-block rounded-[8px] bg-[var(--brand-indigo)] px-3 py-1.5 text-[12px] font-semibold text-white">
              Confirm my appointment
            </span>
            <span className="text-[12px] font-semibold text-[var(--brand-indigo)] underline">Change the time</span>
          </span>
        ) : null}
      </div>
    </div>
  );
}

function CallFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[12px] border border-[var(--border-default)] bg-[#FAFBFE] p-3">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
        How the call opens
      </div>
      <p className="m-0 mt-1.5 text-[12px] leading-[1.7] text-[var(--text-secondary)]">“{children}”</p>
    </div>
  );
}
